'use client';

import React from 'react';

import { Body, Caption } from '@depop/web-thrift-ui/Typography';
import { Filter2RowIcon } from '@depop/web-ui-kit/Icons/Filter2RowIcon';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import { Divider } from '../../../Divider';

import { YMALColourFilter } from './YMALColourFilter';
import { YMALPriceFilter } from './YMALPriceFilter';
import { YMALSaleFilter } from './YMALSaleFilter';
import { YMALSizeFilter } from './YMALSizeFilter';
import styles from './styles.module.css';

import { ActiveFilterButton } from '@/components/ActiveFilterButton/ActiveFilterButton';
import { MobileFilterFooter } from '@/components/Filters/MobileFilterFooter';
import { MobileFilterHeader } from '@/components/Filters/MobileFilterHeader';
import baseFilterStyles from '@/components/Filters/baseFilterStyles.module.css';
import { ModuleHeader, ModuleHeaderSize } from '@/components/ModuleHeader';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { getCurrencySymbolFromCountry } from '@/modules/currencies/helpers';
import { type SizeFiltersResponse } from '@/modules/filters/aliases';
import { FilterLocation } from '@/modules/filters/types';
import { useFiltersTracking } from '@/modules/filters/useFiltersTracking';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { type ProductFilters } from '@/modules/product/types';
import { useSearchDrawer } from '@/modules/search/useSearchDrawer';
import { capitalise } from '@/modules/text/helpers';
import { useCurrentUser } from '@/modules/user/useCurrentUser';
import { toggleInArray } from '@/utils/toggleItemInArray';

type YouMightAlsoLikeFiltersProps = {
  filters: ProductFilters;
  filterOptions: {
    sizes: string[];
    colours: string[];
    priceRange: {
      min: number;
      max: number;
    };
    sizeAggregates: Record<string, number>;
  };
  onSizeFilterChange: (
    sizesOrUpdater: string[] | ((prev: string[] | undefined) => string[])
  ) => void;
  onColourFilterChange: (
    coloursOrUpdater: string[] | ((prev: string[] | undefined) => string[])
  ) => void;
  onPriceFilterChange: (min?: number, max?: number) => void;
  onSaleFilterChange: (onSale?: boolean) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  activeFiltersCount: number;
  resultsCount?: number;
  isFilterableYMALExperiment?: boolean;
  sizeIdToNameMap?: Map<string, string>;
  filteredSizeTree: SizeFiltersResponse;
};

export function YouMightAlsoLikeFilters({
  filters,
  filterOptions,
  onSizeFilterChange,
  onColourFilterChange,
  onPriceFilterChange,
  onSaleFilterChange,
  onClearFilters,
  hasActiveFilters,
  activeFiltersCount,
  resultsCount,
  isFilterableYMALExperiment,
  sizeIdToNameMap,
  filteredSizeTree,
}: YouMightAlsoLikeFiltersProps) {
  const t = useTranslations('common');
  const searchT = useTranslations('search');
  const productT = useTranslations('product');
  const { location } = useCurrentLocation();
  const currencySymbol = getCurrencySymbolFromCountry(location);
  const [, user] = useCurrentUser();

  const hasResults = resultsCount && resultsCount > 0;

  const { isOpen: isFilterDrawerOpen, setIsOpen: setIsFilterDrawerOpen } =
    useSearchDrawer();
  const { sendActivityTrackerEvent } = useActivityTracker({ userId: user?.id });
  useFiltersTracking();

  function closeMobileFilterDrawer() {
    setIsFilterDrawerOpen(false);
  }
  // Build active filter buttons
  function buildActiveFilters() {
    const activeFilters: Array<{ name: string; handleClick: () => void }> = [];

    // Size filters - now using composite IDs, convert to readable names
    if (filters.sizes && filters.sizes.length > 0) {
      filters.sizes.forEach((compositeId) => {
        const sizeName = sizeIdToNameMap?.get(compositeId) || compositeId;
        activeFilters.push({
          name: sizeName,
          handleClick: () =>
            onSizeFilterChange((prev) => toggleInArray(prev, compositeId)),
        });
      });
    }

    // Colour filters
    if (filters.colours && filters.colours.length > 0) {
      filters.colours.forEach((colour) => {
        activeFilters.push({
          name: capitalise(colour),
          handleClick: () =>
            onColourFilterChange((prev) => toggleInArray(prev, colour)),
        });
      });
    }

    // Price filter
    if (filters.minPrice !== undefined || filters.maxPrice !== undefined) {
      let name = '';
      if (filters.minPrice && filters.maxPrice) {
        name = `${currencySymbol}${filters.minPrice} - ${currencySymbol}${filters.maxPrice}`;
      } else if (filters.minPrice) {
        name = `${searchT('MoreThan')} ${currencySymbol}${filters.minPrice}`;
      } else if (filters.maxPrice) {
        name = `${searchT('LessThan')} ${currencySymbol}${filters.maxPrice}`;
      }
      activeFilters.push({
        name,
        handleClick: () => onPriceFilterChange(undefined, undefined),
      });
    }

    // Sale filter
    if (filters.onSale) {
      activeFilters.push({
        name: searchT('OnSale'),
        handleClick: () => onSaleFilterChange(false),
      });
    }

    return activeFilters;
  }

  const activeFilters = buildActiveFilters();

  function handleFilterOpenClick(type: FilterLocation) {
    sendActivityTrackerEvent(ActivityTrackerEventType.FILTER_OPENED_ACTION, {
      filterType: type,
    });
    setIsFilterDrawerOpen((prev) => !prev);
  }

  return (
    <div
      className={clsx(
        styles.container,
        !hasActiveFilters && styles.noActiveFilters
      )}
    >
      <ModuleHeader
        className={styles.header}
        title={t('YouMightAlsoLike')}
        size={ModuleHeaderSize.Small}
      />
      <Divider className={styles.divider} aria-hidden="true" />
      <div
        className={clsx(baseFilterStyles.filterAndSortContainer, [
          styles.ymalFilterContainer,
        ])}
      >
        <span className={styles.ymaTitleWrapper}>
          <button
            onClick={() => handleFilterOpenClick(FilterLocation.Mobile)}
            className={styles.mobileFilterButton}
          >
            <Filter2RowIcon size={22} />
            <Body as="span" variant="md-bold">
              {t('Filters')}
            </Body>
            {activeFiltersCount > 0 && (
              <Caption
                as="span"
                colour="base"
                variant="lg-regular"
                className={styles.filterCount}
              >
                {activeFiltersCount}
              </Caption>
            )}
          </button>
          {!!hasResults && (
            <Body as="span" className={styles.mobileResultsCount}>
              {productT('YMALFilters.ResultsCount', {
                count: resultsCount,
              })}
            </Body>
          )}
        </span>

        <div
          className={clsx(
            styles.filtersContainer,
            hasActiveFilters && styles.withActiveFilters,
            !isFilterDrawerOpen && styles.hidden
          )}
        >
          <MobileFilterHeader
            closeMobileFilterDrawer={closeMobileFilterDrawer}
            title={t('Filters')}
          />

          <div className={styles.dropdownsContainer}>
            {/* Size Filter */}
            <YMALSizeFilter
              sizes={filters.sizes}
              sizeAggregates={filterOptions.sizeAggregates}
              onSizeFilterChange={onSizeFilterChange}
              onClearFilters={onClearFilters}
              closeMobileFilterDrawer={closeMobileFilterDrawer}
              isFilterableYMALExperiment={isFilterableYMALExperiment}
              filteredSizeTree={filteredSizeTree}
            />

            {/* Colour Filter */}
            <YMALColourFilter
              colours={filters.colours}
              colourOptions={filterOptions.colours}
              onColourFilterChange={onColourFilterChange}
              onClearFilters={onClearFilters}
              closeMobileFilterDrawer={closeMobileFilterDrawer}
              isFilterableYMALExperiment={isFilterableYMALExperiment}
            />

            {/* Price Filter */}
            <YMALPriceFilter
              minPrice={filters.minPrice}
              maxPrice={filters.maxPrice}
              priceRange={filterOptions.priceRange}
              onPriceFilterChange={onPriceFilterChange}
              onClearFilters={onClearFilters}
              closeMobileFilterDrawer={closeMobileFilterDrawer}
              isFilterableYMALExperiment={isFilterableYMALExperiment}
            />

            {/* Sale Filter */}
            <YMALSaleFilter
              onSale={filters.onSale}
              onSaleFilterChange={onSaleFilterChange}
            />
          </div>

          {/* empty div to fill the space on mobile in lieu of dropdown content */}
          <div className={styles.flexGrow} />

          <MobileFilterFooter
            handleClear={onClearFilters}
            handleSubmit={closeMobileFilterDrawer}
            isFilterableYMALExperiment={isFilterableYMALExperiment}
            hideResultsCount={true}
          />

          {/* Results Count */}
          {!!hasResults && (
            <Body as="span" className={styles.resultsCount}>
              {productT('YMALFilters.ResultsCount', {
                count: resultsCount,
              })}
            </Body>
          )}
        </div>

        {/* Active Filters and Clear All - inline on desktop and mobile */}
        {(activeFilters.length > 0 || hasActiveFilters) && (
          <div className={styles.activeFiltersRow}>
            {activeFilters.length > 0 && (
              <div className={styles.activeFilterWrapper}>
                {activeFilters.map((activeFilter, index) => (
                  <ActiveFilterButton
                    key={`${activeFilter.name}-${index}`}
                    {...activeFilter}
                    isFilterableYMALExperiment={isFilterableYMALExperiment}
                  />
                ))}
              </div>
            )}
            {hasActiveFilters && (
              <button
                onClick={onClearFilters}
                className={styles.clearAllButton}
              >
                <Body as="span" className={styles.clearAllButtonText}>
                  {productT('YMALFilters.ClearAll')}
                </Body>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
